import { Check, Layers } from 'lucide-react'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { TONES, halftoneLayers, monogram, type ListingTone, type TornadoCard } from '@/components/landing/listing'
import type { IndustryData, Template, Workflow } from '@/data/industries'
import { APP_ICON } from './listing'

/* ------------------------------------------------------------------------
   Document cards for the card tornado, built from an industry's templates
   (forms, schedules, letters) and its workflow outputs (packages). Sized in
   em so the tornado's font-size scales them.
------------------------------------------------------------------------ */

const CARD_TONES: ListingTone[] = ['purple', 'blue', 'pink', 'black', 'green']

/** Generic form fields for a document card, by the words in its name. */
function fieldsFor(name: string): string[] {
  const n = name.toLowerCase()
  if (n.includes('claim')) return ['claim_no', 'date_of_loss', 'insured_name']
  if (n.includes('certificate')) return ['certificate_holder', 'policy_no', 'effective_date']
  if (n.includes('renewal')) return ['policyholder', 'renewal_date', 'premium']
  if (n.includes('filing')) return ['state', 'form_no', 'filing_date']
  if (n.includes('underwriting')) return ['applicant', 'risk_class', 'limit']
  return ['insured_name', 'policy_no', 'effective_date']
}

function TemplateFace({ template, tone }: { template: Template; tone: ListingTone }) {
  const spec = TONES[tone]
  const app = APP_ICON[template.type]
  return (
    <div className="flex h-full w-full flex-col bg-white text-[#0a0a0a] shadow-[0_1.2em_2em_-1.2em_rgba(10,10,10,0.45)]">
      <div className="relative flex items-center justify-between overflow-hidden px-[0.9em] py-[0.7em]" style={{ backgroundColor: spec.bg, color: spec.fg }}>
        <span className="relative font-[family-name:var(--font-landing-subdisplay)] text-[0.62em] font-bold uppercase tracking-[0.08em]">
          {template.type}
        </span>
        {app && (
          <span className="relative flex h-[1.7em] w-[1.7em] items-center justify-center bg-white">
            <OfficeAppIcon app={app} className="h-[1.35em] w-[1.35em]" />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-[0.65em] p-[0.9em]">
        <p className="font-[family-name:var(--font-landing-display)] text-[0.82em] uppercase leading-[1.3] tracking-[-0.01em]">{template.name}</p>
        <p className="text-[0.62em] leading-tight text-[#0a0a0a]/60">{template.description}</p>
        {template.type === 'Excel' ? (
          <span className="mt-[0.2em] grid grid-cols-3 gap-px bg-[#0a0a0a]/15 text-[0.5em]">
            {Array.from({ length: 15 }).map((_, i) => (
              <span key={i} className="block h-[1.9em] bg-white" style={i < 3 ? { backgroundColor: '#f0f0f0' } : i === 10 ? { backgroundColor: spec.bg } : undefined} />
            ))}
          </span>
        ) : (
          <dl className="mt-[0.2em] grid gap-[0.45em]">
            {fieldsFor(template.name).map((field) => (
              <div key={field} className="flex flex-col gap-[0.2em]">
                <dt className="text-[0.48em] font-bold uppercase tracking-[0.08em] text-[#0a0a0a]/50">{field.replace(/_/g, ' ')}</dt>
                <dd className="border-b border-[#0a0a0a]/25 pb-[0.15em] font-[family-name:var(--font-landing-mono)] text-[0.52em] text-[var(--color-landing-accent)]">
                  {`{{${field}}}`}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <span className="mt-auto flex items-center justify-between border-t border-[#0a0a0a]/15 pt-[0.5em]">
          <span className="block h-[0.3em] w-[40%] bg-[#0a0a0a]/15" />
          <span className="block h-[0.3em] w-[22%]" style={{ backgroundColor: spec.bg }} />
        </span>
      </div>
    </div>
  )
}

function PackageFace({ workflow, tone }: { workflow: Workflow; tone: ListingTone }) {
  const spec = TONES[tone]
  const [fine, coarse] = halftoneLayers(spec.dot, 160)
  return (
    <div className="relative flex h-full w-full flex-col justify-between overflow-hidden p-[1em]" style={{ backgroundColor: spec.bg, color: spec.fg }}>
      <div className="absolute inset-0" style={fine} />
      <div className="absolute inset-0" style={coarse} />
      <Layers className="relative h-[1.8em] w-[1.8em]" strokeWidth={1.75} />
      <ul className="relative grid gap-[0.35em]">
        {workflow.steps.slice(0, 4).map((step) => (
          <li key={step} className="flex items-center gap-[0.4em] text-[0.56em] font-semibold leading-tight">
            <span className="flex h-[1.3em] w-[1.3em] shrink-0 items-center justify-center bg-white text-[#0a0a0a]">
              <Check className="h-[0.9em] w-[0.9em]" strokeWidth={3} />
            </span>
            {step}
          </li>
        ))}
      </ul>
      <p className="relative font-[family-name:var(--font-landing-display)] text-[0.8em] uppercase leading-[1.3]">{workflow.output}</p>
    </div>
  )
}

function Back({ label, tone }: { label: string; tone: ListingTone }) {
  const spec = TONES[tone]
  const [fine, coarse] = halftoneLayers(spec.dot, 20)
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden" style={{ backgroundColor: spec.bg, color: spec.fg }}>
      <div className="absolute inset-0" style={fine} />
      <div className="absolute inset-0" style={coarse} />
      <span className="relative font-[family-name:var(--font-landing-display)] text-[2.4em] leading-none opacity-80">{monogram(label)}</span>
    </div>
  )
}

/** Tornado cards: every template, then every workflow's output package, interleaved. */
export function documentTornadoCards(industry: IndustryData): TornadoCard[] {
  const templates = industry.templates.map((template, i) => {
    const tone = CARD_TONES[i % CARD_TONES.length]!
    return {
      id: `t-${template.name}`,
      front: <TemplateFace template={template} tone={tone} />,
      back: <Back label={template.name} tone={tone} />,
    }
  })
  const packages = industry.workflows.map((workflow, i) => {
    const tone = CARD_TONES[(i + 2) % CARD_TONES.length]!
    return {
      id: `w-${workflow.name}`,
      front: <PackageFace workflow={workflow} tone={tone} />,
      back: <Back label={workflow.output} tone={tone} />,
    }
  })
  // Interleave: two templates, one package, so the helix alternates.
  const cards: TornadoCard[] = []
  let p = 0
  templates.forEach((card, i) => {
    cards.push(card)
    if (i % 2 === 1 && packages[p]) cards.push(packages[p++]!)
  })
  return [...cards, ...packages.slice(p)]
}
